import { bridge } from "@dinahmoe/dmafjs-core";
import { browserRuntime } from "@dinahmoe/dmafjs-web";
import { loaders } from "./loaders";
import { assetDictionary } from "./assetDictionary";
import { assetQueue } from "./assetQueue";
import { normalize, retrieveFilenameFromPath } from "./utils/path";

class AssetLoaderEngine {
  constructor() {
    browserRuntime.registerEngine("assets", {
      create: this.create.bind(this),
      update: this.update.bind(this),
    });
  }

  create() {}

  update({ uid, key, value }) {
    switch (key) {
      case "load":
        assetQueue.append(
          this.load.bind(this),
          uid,
          value.files,
          value.ignoreFolder,
          value.path
        );
        break;
      case "unload":
        assetQueue.append(
          this.unload.bind(this),
          uid,
          value.files,
          value.ignoreFolder
        );
        break;
      case "destroy":
        assetQueue.append(
          this.destroy.bind(this),
          uid,
          value.files,
          value.ignoreFolder
        );
        break;
    }
  }

  async load(loaderUid, files, ignoreFolder, path) {
    bridge.update({
      uid: loaderUid,
      key: "loading",
      sender: "browser",
    });

    await this.doLoad(loaderUid, files, ignoreFolder, path);

    // All files are loaded or failed, the asset loader is "complete"
    bridge.update({
      uid: loaderUid,
      key: "complete",
      sender: "browser",
    });
  }

  async doLoad(loaderUid, files, ignoreFolder, path, extraFile = false) {
    // All files defined in the asset loader configuration should be described
    // in a way that we can understand. The general rule is `<type>//<uid>`.
    const fileDescriptions = files.map((file) => {
      const pathname = file.pathname;

      try {
        new URL(pathname, "http://localhost");
      } catch {
        throw new Error("Malformed description. File pathname is incorrect");
      }

      const type = file.type;
      const uid = file.assetId ?? retrieveFilenameFromPath(pathname);
      return { pathname, type, uid };
    });

    await Promise.all(fileDescriptions.map(async fileDescription => {
      const { pathname, type, uid } = fileDescription;
      try {
        const key = loaders.createKeyByType(type, uid);
        let completeAssetPath = pathname;
        if (!extraFile) {
          const normalizedPathname = normalize(pathname);
          completeAssetPath = getAssetUrl(`${path}${normalizedPathname}`);
        }
        const file = await loaders.loadByType(
            type,
            uid,
            completeAssetPath,
            ignoreFolder
        );

        // Save the content here if theres a need for a detached representation of the file.
        if (file.representation) {
          assetDictionary.set(key, file.content);
        }

        //If loaded file contains instructions about loading other files its configs are in "filesToLoad" property
        if (file.filesToLoad) {
          //assetsPath is empty string because loader is responsible to construct absolute path for extra files
          await this.doLoad(loaderUid, file.filesToLoad, ignoreFolder, '', true);
        }

        // Announce the completion of the file
        bridge.update({
          uid: loaderUid,
          key: "file",
          value: {
            content: file.representation || file.content,
            key,
            extraFile
          },
          sender: "browser",
        });
      } catch (err) {
        console.error(err);
        console.error(`Unable to load file ${uid} as type ${type}`);
      }
    }));
  }

  async unload(instanceUid, files) {
    bridge.update({
      uid: instanceUid,
      key: "unloading",
      sender: "browser",
    });

    files.map((file) => {
      const type = file.type;
      const uid = file.assetId ?? retrieveFilenameFromPath(file.pathname);
      const key = loaders.createKeyByType(type, uid);
      assetDictionary.remove(key);
      bridge.update({
        uid: instanceUid,
        key: "unload",
        value: { key },
        sender: "browser",
      });
    });
    bridge.update({
      uid: instanceUid,
      key: "unloaded",
      sender: "browser",
    });
  }

  async destroy(instanceUid, files) {
    bridge.update({
      uid: instanceUid,
      key: "destroying",
      sender: "browser",
    });

    await this.unload(instanceUid, files);

    bridge.update({
      uid: instanceUid,
      key: "destroyed",
      sender: "browser",
    });
  }
}

// either the pathname is url, or a pathname
function getAssetUrl(pathname) {
  try {
    return new URL(pathname).href;
  } catch {
    return pathname;
  }
}

const assetLoaderEngine = new AssetLoaderEngine();
export { assetLoaderEngine, AssetLoaderEngine };
